import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import SvgIcon from "../SvgIcon";
import submit from "submit-form";
import ItemWrapper from "./ActivityItems";
import ActivityFiltersModal from "./ActivityFiltersModal";
import ProductTabs from "./ProductTabs";
import ActivitySkeleton from "./ActivitySkeleton";
import { getRoleNameById } from "../../views/dashboard/settings/manage-coaches/utils/manage-coaches-utils";
import { RillContext, useRillContext } from "../../rill/RillReact/share/base";

const productTypes = {
  All: "All",
  PULL: "PULL",
  PUSH: "PUSH",
  PRESS: "PRESS",
};

const groupActionsByDay = (actions, t) => {
  const today = moment().startOf("day");
  const yesterday = moment().subtract(1, "day").startOf("day");

  const formatDayLabel = (timestamp) => {
    const date = moment(timestamp);
    if (date.isSame(today, "day")) return t("activity:today");
    if (date.isSame(yesterday, "day")) return t("activity:yesterday");
    return date.format("ddd MMM D");
  };

  return Object.values(
    actions.reduce((acc, action) => {
      const dayLabel = formatDayLabel(action.timestamp);
      acc[dayLabel] = acc[dayLabel] || { day: dayLabel, actions: [] };
      acc[dayLabel].actions.push(action);
      return acc;
    }, {})
  );
};

const Backdrop = ({ close }) => (
  <div className="drawer-backdrop" onClick={close} />
);

const ActivityDrawer = (_) => {
  const { t } = useTranslation("activity");
  const { locals } = useRillContext();

  const {
    query,
    path,
    activityFeed,
    athlete,
    activeUser,
    organizationRoles,
    fromMetric,
    withWeight,
  } = locals;
  const { page = 0, productType, clientId, isOwner } = query;
  const { hasNextPage, list } = activityFeed || {};

  const [openedFilterModal, setOpenedFilterModal] = useState(false);
  const [rawActivities, setRawActivities] = useState([]);
  const [loading, setIsLoading] = useState(true);

  const activitiesToDisplay = useMemo(
    () => groupActionsByDay(rawActivities, t),
    [rawActivities, t]
  );
  useEffect(() => {
    if (list) {
      if (!page || page == 0) {
        setRawActivities(list);
      } else {
        setRawActivities((prevState) => {
          return [...prevState, ...list];
        });
      }
      setIsLoading(false);
    } else setIsLoading(true);
  }, [list]);
  const roleName = getRoleNameById(
    organizationRoles,
    activeUser.organizationRoleId
  );
  // Who gets the "show all clients/athletes activity" toggle. A Team Coach never
  // has clients of their own — there's no "my activity" to fall back to — so the
  // feed is always org-wide for them (see load/activity-feed.js) and the toggle,
  // which would only ever show an empty state if turned off, is hidden entirely.
  // Mirrors the same fix on mobile's AllActivityFeedScreen.
  const isOrganizationOwner = roleName === "Owner";
  const webAppTheme = activeUser.webAppTheme || "dark";

  const close = () =>
    submit("/actions/refresh", {
      method: "GET",
      body: {
        "modal[type]": "",
        page: "",
        productType: "",
        clientId: "",
        isOwner: "",
        actionTypes: "",
      },
    });

  const loadMore = (e) => {
    e.preventDefault();
    submit("/actions/refresh", { method: "GET", body: { page: page + 1 } });
  };

  return (
    <>
      <div className={`activity side-drawer open`}>
        <div className="drawer-main">
          <header>
            <hgroup>
              <p className="s1">{t("title")}</p>
              <SvgIcon
                src="/img/new-icons/close-modal.svg"
                onClickFn={close}
                noFilterOnLight
              />
            </hgroup>
            <nav role="tablist">
              <ProductTabs locals={locals} productType={productType} />
              <button
                data-button-type="secondary"
                data-icon
                onClick={() => setOpenedFilterModal(true)}
              >
                <SvgIcon src="/img/new-icons/filter-white.svg" />
                {t("filter")}
              </button>
            </nav>
            {path?.includes("athlete-portal") && (
              <div className="flex items-center gap-2 py-4 px-6 border-solid border-(--white-20) border-1b">
                <input
                  type="checkbox"
                  name="clientid"
                  onChange={() =>
                    submit("/actions/refresh", {
                      method: "GET",
                      body: { clientId: !clientId },
                    })
                  }
                  defaultChecked={clientId}
                />
                <label htmlFor="clientid">
                  {t("seeAthleteActivityOnly", { name: athlete?.name })}
                </label>
              </div>
            )}
            {isOrganizationOwner && (
              <div className="flex items-center gap-2 py-4 px-6 border-solid border-(--white-20) border-1b">
                <input
                  type="checkbox"
                  name="owner"
                  onChange={() =>
                    submit("/actions/refresh", {
                      method: "GET",
                      body: { isOwner: !isOwner },
                    })
                  }
                  defaultChecked={isOwner}
                />
                <label htmlFor="owner">
                  {activeUser.referAsClients
                    ? t("showAllClientsActivity")
                    : t("showAllAthletesActivity")}
                </label>
              </div>
            )}
          </header>
          {loading && <ActivitySkeleton webAppTheme={webAppTheme} />}
          {list?.length === 0 && (
            <p className="m-auto">{t("noActivities")}</p>
          )}
          {list && activitiesToDisplay.length > 0 && (
            <>
              {activitiesToDisplay.map((day) => (
                <section key={day.day}>
                  <header>
                    <p>{day.day}</p>
                  </header>
                  {day.actions.map((item) => (
                    <ItemWrapper
                      action={item}
                      activeUser={activeUser}
                      key={item._id}
                      productType={productType}
                      fromMetric={fromMetric}
                      withWeight={withWeight}
                    />
                  ))}
                </section>
              ))}
              {hasNextPage && (
                <button
                  data-button-type="outline"
                  onClick={loadMore}
                  className="m-6"
                >
                  <span>{t("loadMore")}</span>
                </button>
              )}
            </>
          )}
        </div>
      </div>
      <ActivityFiltersModal
        openModal={openedFilterModal}
        closeModal={() => setOpenedFilterModal(false)}
        productTypes={productTypes}
      />
      <Backdrop close={close} />
    </>
  );
};

export default ActivityDrawer;
